import { type InterestConfigType, type PropertyFiltersType } from '@aikoa/common'
import { getCurrentLocale } from '@aikoa/i18n'
import { useCurrentLanguage, useTranslations } from '@aikoa/i18n/react'
import { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { InterestDescription } from '~/components/interests/InterestDescription'
import { formatPrice, formatRange, formatRoomsRange } from '~/components/interests/utils'
import {
  GuidedFormSummary,
  type InputSegment,
  type ScriptSegment,
} from '~/components/ui/guided-form-summary'
import { getBuildingTypeLabel, getConditionLabel } from '~/lib/assets'
import { propertyFilterFormObjectSchema } from '~/lib/propertyFormSchemas'
import { cn } from '~/lib/utils'

const locale = getCurrentLocale()
const { shape } = propertyFilterFormObjectSchema

export type InterestFilterSummaryContext = 'own' | 'contact'

export function InterestFilterSummary({
  filters,
  locations,
  selectedEditor,
  onOpenEditor,
  className,
  textClassName,
  renderEditor,
  activatedFilters,
  displayOnly,
  description,
  context = 'own',
}: {
  filters: PropertyFiltersType
  locations: { label: string }[]
  selectedEditor?: InterestConfigType | undefined
  onOpenEditor?: (editor: InterestConfigType | undefined) => void
  className?: string
  textClassName?: string
  renderEditor?: (editor: InterestConfigType) => React.ReactNode
  activatedFilters?: InterestConfigType[]
  displayOnly?: boolean
  description?: string | null
  context?: InterestFilterSummaryContext
}) {
  const { t: tProperty } = useTranslations('property')
  const { t } = useTranslations('interest')
  const language = useCurrentLanguage()

  const buildingTypes = useMemo(() => filters.buildingTypes ?? [], [filters.buildingTypes])

  const conditions = useMemo(() => filters.conditions ?? [], [filters.conditions])

  const formatList = useCallback(
    (items: string[]) => {
      if (items.length > 3) {
        return t(($) => $.filterSummary.itemsOverflow, {
          items: items.slice(0, 3).join(', '),
          count: items.length - 3,
        })
      }
      return items.join(', ')
    },
    [t]
  )

  const inputs: InputSegment<InterestConfigType>[] = useMemo(() => {
    const buildingTypeLabels = buildingTypes.map((type) =>
      getBuildingTypeLabel({ buildingType: type, t: tProperty })
    )

    const conditionLabels = conditions.map((cond) => getConditionLabel(cond, tProperty))

    const locLabels = locations.map((loc) => loc.label)

    return [
      {
        name: 'locations',
        value: locations,
        editor: null,
        isValid: () => locLabels.length > 0,
        renderValue: () => (locLabels.length > 0 ? formatList(locLabels) : '???'),
      },
      {
        name: 'price',
        value: { priceMin: filters.priceMin, priceMax: filters.priceMax },
        editor: null,
        isValid: () => filters.priceMax !== undefined,
        hasError: () =>
          (filters.priceMax !== undefined &&
            !shape.priceMax.safeParse(filters.priceMax).success) ||
          (filters.priceMin !== undefined &&
            !shape.priceMin.safeParse(filters.priceMin).success) ||
          (filters.priceMin !== undefined &&
            filters.priceMax !== undefined &&
            filters.priceMin > filters.priceMax),
        renderValue: () =>
          filters.priceMax ? formatPrice(language, t, filters.priceMin, filters.priceMax) : '???',
      },
      {
        name: 'buildingType',
        value: buildingTypes,
        editor: null,
        isValid: () => buildingTypes.length > 0,
        renderValue: () => (buildingTypeLabels.length > 0 ? formatList(buildingTypeLabels) : '???'),
      },
      {
        name: 'size',
        value: { sizeMin: filters.sizeMin, sizeMax: filters.sizeMax },
        editor: null,
        isValid: () => filters.sizeMin !== undefined,
        hasError: () =>
          (filters.sizeMin !== undefined && !shape.sizeMin.safeParse(filters.sizeMin).success) ||
          (filters.sizeMax !== undefined && !shape.sizeMax.safeParse(filters.sizeMax).success) ||
          (filters.sizeMin !== undefined &&
            filters.sizeMax !== undefined &&
            filters.sizeMin > filters.sizeMax),
        renderValue: () =>
          !filters.sizeMax && !filters.sizeMin
            ? '???'
            : formatRange(
                filters.sizeMin,
                filters.sizeMax,
                t(($) => $.details.sizeUnit)
              ),
      },
      {
        name: 'rooms',
        value: { roomsMin: filters.roomsMin, roomsMax: filters.roomsMax },
        editor: null,
        isValid: () => filters.roomsMin !== undefined,
        hasError: () =>
          (filters.roomsMin !== undefined &&
            !shape.roomsMin.safeParse(filters.roomsMin).success) ||
          (filters.roomsMax !== undefined &&
            !shape.roomsMax.safeParse(filters.roomsMax).success) ||
          (filters.roomsMin !== undefined &&
            filters.roomsMax !== undefined &&
            filters.roomsMin > filters.roomsMax),
        renderValue: () =>
          !filters.roomsMax && !filters.roomsMin
            ? '???'
            : formatRoomsRange(filters.roomsMin, filters.roomsMax, t),
      },
      {
        name: 'condition',
        value: conditions,
        editor: null,
        isValid: () => conditions.length > 0,
        renderValue: () => (conditionLabels.length > 0 ? formatList(conditionLabels) : '???'),
        optional: true,
      },
      {
        name: 'year',
        value: { yearMin: filters.buildYearMin, yearMax: filters.buildYearMax },
        editor: null,
        isValid: () => filters.buildYearMin !== undefined || filters.buildYearMax !== undefined,
        hasError: () => {
          const currentYear = new Date().getFullYear()
          const yearMin = filters.buildYearMin ? Number(filters.buildYearMin) : undefined
          const yearMax = filters.buildYearMax ? Number(filters.buildYearMax) : undefined

          return (
            (yearMin !== undefined &&
              (yearMin < locale.property.min_year || yearMin > currentYear)) ||
            (yearMax !== undefined &&
              (yearMax < locale.property.min_year || yearMax > currentYear))
          )
        },
        renderValue: () =>
          !filters.buildYearMax && !filters.buildYearMin
            ? '???'
            : formatRange(filters.buildYearMin, filters.buildYearMax, ''),
        optional: true,
      },
    ]
  }, [buildingTypes, conditions, locations, filters, tProperty, t, formatList, language])

  const script: ScriptSegment<InterestConfigType>[] = useMemo(
    () => [
      {
        name: 'locations',
        segmentScript: t(($) =>
          context === 'contact'
            ? $.filterSummary.locationsScriptContact
            : $.filterSummary.locationsScript
        ),
        buttonLabel: t(($) =>
          context === 'contact'
            ? $.filterSummary.locationsButtonContact
            : $.filterSummary.locationsButton
        ),
      },
      {
        name: 'price',
        segmentScript: t(($) => $.filterSummary.priceScript),
        buttonLabel: t(($) => $.filterSummary.priceButton),
      },
      {
        name: 'buildingType',
        segmentScript: t(($) => $.filterSummary.buildingTypeScript),
        buttonLabel: t(($) => $.filterSummary.buildingTypeButton),
      },
      {
        name: 'size',
        segmentScript: t(($) => $.filterSummary.sizeScript),
        buttonLabel: t(($) => $.filterSummary.sizeButton),
      },
      {
        name: 'rooms',
        segmentScript: t(($) => $.filterSummary.roomsScript),
        buttonLabel: t(($) => $.filterSummary.roomsButton),
      },
      {
        name: 'condition',
        segmentScript: t(($) => $.filterSummary.conditionScript),
        buttonLabel: t(($) =>
          context === 'contact'
            ? $.filterSummary.conditionButtonContact
            : $.filterSummary.conditionButton
        ),
      },
      {
        name: 'year',
        segmentScript: t(($) =>
          context === 'contact' ? $.filterSummary.yearScriptContact : $.filterSummary.yearScript
        ),
        buttonLabel: t(($) => $.filterSummary.yearButton),
      },
    ],
    [t, context]
  )

  return (
    <View className={cn(className)}>
      <GuidedFormSummary
        inputs={inputs}
        script={script}
        activatedSegments={activatedFilters ?? []}
        selectedSegment={selectedEditor}
        onSelectSegment={onOpenEditor}
        renderEditor={renderEditor}
        displayMode={displayOnly ? 'display-valid' : 'edit'}
        textClassName={textClassName}
      />
      {displayOnly && description && String(description).trim().length > 0 ? (
        <InterestDescription description={String(description).trim()} className={cn('mt-3')} />
      ) : null}
    </View>
  )
}
